import { Users, Plane, Thermometer, Cake } from 'lucide-react'
import { SectionCard } from '../../patterns/SectionCard'
import { Avatar } from '../../patterns/Avatar'

function range(a: string, b: string): string {
  const f = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  return `${f(a)} – ${f(b)}`
}

function Group({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="px-5 py-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground mb-2">{icon}{label}</div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PersonRow({ person, sub, badge }: { person: any; sub: React.ReactNode; badge?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <Avatar name={person?.name} src={person?.avatarUrl} className="h-6 w-6 text-[10px] shrink-0" />
      <span className="flex-1 min-w-0 text-sm truncate">{person?.name}</span>
      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{sub}</span>
      {badge}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function TeamCard({ team }: { team: any | null }) {
  const empty = !team || (team.vacations?.length === 0 && team.sickToday?.length === 0 && team.birthdays?.length === 0)
  return (
    <SectionCard icon={<Users size={14} />} title="Team" fill noBodyPadding>
      {empty ? (
        <div className="px-5 py-6 text-center text-xs text-muted-foreground/70">All present — no upcoming leave or birthdays.</div>
      ) : (
        <div className="divide-y divide-border/60">
          {team.vacations?.length > 0 && (
            <Group icon={<Plane size={12} />} label="Vacation soon">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {team.vacations.map((v: any, i: number) => (
                <PersonRow key={i} person={v.person} sub={range(v.startDate, v.endDate)}
                  badge={<span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">{v.type}</span>} />
              ))}
            </Group>
          )}
          {team.sickToday?.length > 0 && (
            <Group icon={<Thermometer size={12} />} label="Out sick today">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {team.sickToday.map((v: any, i: number) => (
                <PersonRow key={i} person={v.person} sub={range(v.startDate, v.endDate)}
                  badge={<span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500">SICK</span>} />
              ))}
            </Group>
          )}
          {team.birthdays?.length > 0 && (
            <Group icon={<Cake size={12} />} label="Birthdays">
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {team.birthdays.map((b: any, i: number) => (
                <PersonRow key={i} person={b.person}
                  sub={b.daysUntil === 0
                    ? <span className="text-primary font-medium">today</span>
                    : new Date(Date.UTC(2000, (b.month ?? 1) - 1, b.day ?? 1)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} />
              ))}
            </Group>
          )}
        </div>
      )}
    </SectionCard>
  )
}
